import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal, UserRound } from 'lucide-react';
import { LiveChatPanel } from '@/components/support/LiveChatPanel';
import { Button } from '@/components/ui/button';
import { useSupportAuth } from '@/context/SupportAuthContext';
import { usePresence } from '@/hooks/usePresence';
import { sendChatMessage } from '@/services/chatService';
import {
  playNotificationSound,
  showBrowserNotification,
} from '@/services/notificationService';
import { parseUserAgent } from '@/services/supportUtils';
import {
  closeSupportConversation,
  listActiveAgents,
  subscribeAgentActiveChats,
  subscribeWaitingRequests,
  transferSupportRequest,
  acceptSupportRequest,
  getRequestWithSecret,
} from '@/services/supportService';
import type { SupportAgent, SupportRequest } from '@/types/support';
import { cn } from '@/lib/utils';

export function SupportDashboardPage() {
  const { user, agent } = useSupportAuth();
  const [params, setParams] = useSearchParams();
  const [waiting, setWaiting] = useState<SupportRequest[]>([]);
  const [mine, setMine] = useState<SupportRequest[]>([]);
  const [agents, setAgents] = useState<SupportAgent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(params.get('chat'));
  const [transferTo, setTransferTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const u1 = subscribeWaitingRequests((list) => {
      setWaiting((prev) => {
        if (list.length > prev.length) {
          playNotificationSound();
          showBrowserNotification('New support request', list[list.length - 1]?.issueSummary || 'Customer waiting');
        }
        return list;
      });
    });
    const u2 = subscribeAgentActiveChats(user.uid, setMine);
    void listActiveAgents().then((a) => setAgents(a.filter((x) => x.agentId !== user.uid)));
    return () => {
      u1();
      u2();
    };
  }, [user?.uid]);

  useEffect(() => {
    const chat = params.get('chat');
    setSelectedId(chat);
  }, [params]);

  const selected = useMemo(
    () => mine.find((r) => r.requestId === selectedId) || null,
    [mine, selectedId],
  );

  const customerPresence = usePresence(selected?.customerId);
  const showChat = Boolean(selected);

  const selectChat = (id: string) => {
    setSelectedId(id);
    setParams({ chat: id });
    setDetailsOpen(false);
  };

  const clearChat = () => {
    setSelectedId(null);
    setParams({});
    setDetailsOpen(false);
  };

  const claimFromQueue = async (requestId: string) => {
    if (!user || !agent) return;
    setBusy(true);
    setError(null);
    try {
      const data = await getRequestWithSecret(requestId);
      if (!data?.accessToken) {
        setError('Could not load secure token for this request.');
        return;
      }
      const result = await acceptSupportRequest({
        requestId,
        agentId: user.uid,
        accessToken: data.accessToken,
        agentName: agent.name,
      });
      if (!result.ok) {
        setError(
          result.reason === 'already_assigned'
            ? 'This chat has already been assigned.'
            : 'Could not accept this request.',
        );
        return;
      }
      selectChat(requestId);
    } finally {
      setBusy(false);
    }
  };

  const deviceLabel = selected
    ? (() => {
        const { browser, device } = parseUserAgent(selected.browserInfo.userAgent);
        return `${device} · ${browser}`;
      })()
    : '';

  return (
    <div className="h-full min-h-0 flex flex-col md:flex-row overflow-hidden">
      {/* Inbox list - hidden on mobile when a chat is open */}
      <section
        className={cn(
          'w-full md:w-80 lg:w-96 border-border flex flex-col min-h-0 shrink-0 bg-background',
          'md:border-r',
          showChat ? 'hidden md:flex' : 'flex flex-1',
        )}
      >
        <div className="p-4 border-b border-border shrink-0">
          <h1 className="text-lg font-semibold tracking-tight">Inbox</h1>
          <p className="text-xs text-muted-foreground mt-1">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {waiting.length} waiting
            </span>
            <span className="mx-2 text-border">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {mine.length} active
            </span>
          </p>
          {error && <p className="text-xs text-destructive mt-2">{error}</p>}
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="px-4 pt-4 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Waiting queue
          </div>
          {waiting.length === 0 && (
            <p className="px-4 py-4 text-sm text-muted-foreground">No customers waiting.</p>
          )}
          {waiting.map((r) => (
            <button
              key={r.requestId}
              type="button"
              onClick={() => void claimFromQueue(r.requestId)}
              disabled={busy}
              className="w-full text-left px-4 py-3.5 border-b border-border/50 hover:bg-muted/50 active:bg-muted/70 transition-colors disabled:opacity-50"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 flex items-center justify-center shrink-0">
                  <UserRound className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate">{r.customerName || 'Visitor'}</div>
                    <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300 shrink-0">Accept</span>
                  </div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{r.issueSummary}</div>
                  <div className="text-[10px] text-muted-foreground mt-1.5">
                    {new Date(r.createdAt).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </button>
          ))}

          <div className="px-4 pt-5 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Your chats
          </div>
          {mine.length === 0 && (
            <p className="px-4 py-4 text-sm text-muted-foreground">No active chats.</p>
          )}
          {mine.map((r) => (
            <button
              key={r.requestId}
              type="button"
              onClick={() => selectChat(r.requestId)}
              className={cn(
                'w-full text-left px-4 py-3.5 border-b border-border/50 hover:bg-muted/50 active:bg-muted/70 transition-colors',
                selectedId === r.requestId && 'bg-muted/70',
              )}
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <UserRound className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.customerName || 'Visitor'}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{r.issueSummary}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Chat pane - full screen on mobile when selected */}
      <section
        className={cn(
          'flex-1 min-w-0 min-h-0 flex-col bg-background',
          showChat ? 'flex' : 'hidden md:flex',
        )}
      >
        {!selected || !user || !agent ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground p-8 text-center">
            <UserRound className="h-10 w-10 opacity-30 mb-1" />
            <p className="font-medium text-foreground">No chat selected</p>
            <p className="max-w-xs">Claim a waiting request or open an active chat from the inbox.</p>
          </div>
        ) : (
          <>
            <header className="shrink-0 border-b border-border">
              <div className="flex items-center gap-2 px-2 sm:px-4 py-2.5">
                <button
                  type="button"
                  onClick={clearChat}
                  className="md:hidden p-2 rounded-lg text-muted-foreground hover:bg-muted/60"
                  aria-label="Back to inbox"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm sm:text-base font-semibold truncate">
                      {selected.customerName || 'Anonymous visitor'}
                    </h2>
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full shrink-0',
                        customerPresence?.online ? 'bg-emerald-500' : 'bg-slate-400',
                      )}
                      title={customerPresence?.online ? 'Online' : 'Offline'}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {customerPresence?.online ? 'On page' : 'Offline'} · {deviceLabel}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="p-2 rounded-lg text-muted-foreground hover:bg-muted/60"
                  aria-label="Chat actions"
                >
                  <MoreHorizontal className="h-5 w-5" />
                </button>
              </div>

              {detailsOpen && (
                <div className="px-4 pb-3 space-y-3 border-t border-border/60 bg-muted/20">
                  {error && (
                    <p className="text-xs text-destructive pt-3">{error}</p>
                  )}
                  <p className={cn('text-sm text-muted-foreground', !error && 'pt-3')}>{selected.issueSummary}</p>
                  <a
                    className="text-[11px] text-muted-foreground underline underline-offset-2 break-all block"
                    href={selected.pageUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selected.pageUrl}
                  </a>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                    <select
                      className="h-9 rounded-md border border-border bg-background px-2 text-xs flex-1 min-w-0"
                      value={transferTo}
                      onChange={(e) => setTransferTo(e.target.value)}
                    >
                      <option value="">Transfer to…</option>
                      {agents.map((a) => (
                        <option key={a.agentId} value={a.agentId}>{a.name}</option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 sm:flex-none"
                        disabled={!transferTo || busy}
                        onClick={() => {
                          void (async () => {
                            setBusy(true);
                            await transferSupportRequest({
                              requestId: selected.requestId,
                              fromAgentId: user.uid,
                              toAgentId: transferTo,
                            });
                            setBusy(false);
                            clearChat();
                          })();
                        }}
                      >
                        Transfer
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1 sm:flex-none"
                        disabled={busy}
                        onClick={() => {
                          void (async () => {
                            setBusy(true);
                            setError(null);
                            try {
                              await closeSupportConversation({
                                requestId: selected.requestId,
                                agentId: user.uid,
                                agentName: agent.name,
                              });
                              clearChat();
                            } catch (e) {
                              setError(e instanceof Error ? e.message : 'Failed to close conversation');
                              setDetailsOpen(true);
                            } finally {
                              setBusy(false);
                            }
                          })();
                        }}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </header>

            <LiveChatPanel
              requestId={selected.requestId}
              currentUid={user.uid}
              peerUid={selected.customerId}
              peerName={selected.customerName || 'Customer'}
              role="agent"
              className="flex-1 min-h-0"
              onSend={async (text) => {
                await sendChatMessage({
                  requestId: selected.requestId,
                  senderId: user.uid,
                  senderRole: 'agent',
                  text,
                });
              }}
            />
          </>
        )}
      </section>
    </div>
  );
}
